import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import StudyUsers from '@/components/Studies/Show/Users';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { api as apiRoutes, web as webRoutes } from '@/support/route';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StudyTableProps } from './Dashboard.model';
import {
  generateColumns,
  generateStudiesMetadataColumns,
  studiesfilterOptions,
  updateColumns,
} from './Dashboard.utils';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import type { StudyForm } from 'model/StudyForm.model';
import { useFetchCollection } from '@/support/Hooks/fetch';

const initColumns: Array<TanStackColumnDef<Study>> = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: Study } }) => (
      <Link
        className="link"
        to={webRoutes('studies.show', {
          id: row.original.id,
        })}
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'code',
    header: 'Study Code',
    accessorKey: 'code',
    enableSorting: true,
    isVisible: false,
  },
  {
    id: 'type',
    header: 'Type',
    isVisible: true,
    enableSorting: true,
    accessorFn: (row: Study) => row.settings?.title,
  },
  {
    id: 'team',
    header: 'Team',
    enableSorting: false,
    isVisible: true,
    accessorKey: 'users',
    cell: ({ row }: { row: { original: Study } }) => <StudyUsers study={row.original} />,
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: Study } }) => <DateTimeRenderer value={row.original.created_at} />,
  },
  {
    id: 'archived_at',
    header: 'Completed',
    accessorKey: 'archived_at',
    enableSorting: true,
    isVisible: true,
    cell: ({ row }: { row: { original: Study } }) => <DateTimeRenderer value={row.original.archived_at} />,
  },
];

const ArchivedTable: React.FC<StudyTableProps> = ({ studyTypes, studyMetadataFilters }) => {
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [columns, setColumns] = useState<Array<TanStackColumnDef<Study>>>(initColumns);
  const [apiError, setAPIError] = useState<boolean>(false);
  const [studyIDVisible, setStudyIDVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const { collection: studyForms, collectionLoading: studyFormsFetching } = useFetchCollection<StudyForm>({
    collectionType: 'teamStudiesForms',
    queryParams: { perPage: -1 },
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (studyTypes) {
      setFilterOptions(studiesfilterOptions(studyTypes, studyMetadataFilters));
    }
  }, [studyTypes]);

  useEffect(() => {
    if (_notNil(studyMetadataFilters) && !studyFormsFetching) {
      generateStudiesMetadataColumns(studyMetadataFilters)
        .then((metadata) => {
          setColumns(
            generateColumns(
              initColumns,
              metadata.map((m) => ({ id: m.id, header: m.header ?? '' })),
              navigate,
              studyForms
            )
          );
          setLoading(false);
        })
        .catch((error) => {
          if (notAborted(error)) {
            setLoading(false);
            setAPIError(error);
          }
        });
    }
  }, [studyMetadataFilters, studyFormsFetching]);

  if (loading) {
    return null;
  }

  return (
    <>
      {apiError ? (
        <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={apiError} />
      ) : (
        <div className="pa4">
          <APITable
            columns={columns}
            apiInfo={{ type: 'legacyInternalApi', route: apiRoutes('studies.archived') }}
            filterOptions={filterOptions}
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(studyMetadataFilters ?? [])}
            onFetchTableData={(studies) => {
              if (!studyIDVisible) {
                const updatedColumns = updateColumns(studies, columns);
                if (_isNotEmpty(updatedColumns)) {
                  setStudyIDVisible(true);
                  setColumns(updatedColumns);
                }
              }
            }}
            searchPlaceholderText="Search by study name or study code"
          />
        </div>
      )}
    </>
  );
};

export default ArchivedTable;
